import React, { useEffect } from 'react'
import { useLocation, useSessionStorage } from 'react-use'
import { LoadingPage } from '@global/loading'
import { useAppSelector } from '@redux/hooks'
import { useExternalLogin } from './hooks/useExternalLogin'

interface ExternalLoginGuardProps {
  children: React.ReactNode
}

/**
 * Guards application routes and handles external application login flows.
 * Supports both legacy API key authentication and hybrid Auth0 JWT authentication.
 *
 * @param props - Component props
 * @returns React component
 */
export function ExternalLoginGuard({ children }: ExternalLoginGuardProps) {
  const location = useLocation()
  const isLoggedIn = !!useAppSelector(state => state.Auth.user)
  const params = new URLSearchParams(location.search)
  const externalLoginParam = params.get('excel-login') === 'true'

  const [isExternalLogin] = useSessionStorage<boolean>('isExternalLogin', externalLoginParam)

  const { initializeLogin } = useExternalLogin(isLoggedIn)

  useEffect(() => {
    if (isExternalLogin) initializeLogin()
  }, [isExternalLogin, initializeLogin])

  /**
   * We will show the loading page if the user is not logged in and is external login
   * because the login will be performed in Auth0, and we just need to wait for the redirect
   * to happen.
   */
  if (isExternalLogin && !isLoggedIn) return <LoadingPage />

  return <>{children}</>
}
